import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  getActiveCompetitions,
  getUpcomingCompetitions,
  getRecentWinners,
  getHallOfFame,
  getRecords,
  formatCompetitionDates,
  getTimeRemaining,
  getCompetitionPercent,
  competitionTypeLabels,
  competitionTypeIcons,
  tournamentLabels,
  tournamentIcons,
  votingLabels,
  votingIcons,
  type RfwCompetition,
  type CompetitionType,
  type HallOfFameEntry,
} from '@/mocks/rfwCompetitions';
import { trendConfig } from '@/mocks/rfwScores';

type MainTab = 'activas' | 'proximas' | 'ganadores' | 'hall-of-fame';

/* ─── Helpers ─── */
function formatCompact(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ─── VS Card ─── */
function VsBadge() {
  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full bg-accent-500 flex items-center justify-center shadow-xl border-2 border-background-50">
      <span className="text-background-50 dark:text-foreground-950 font-black text-sm md:text-base">VS</span>
    </div>
  );
}

/* ─── Competition Progress Bar ─── */
function CompetitionProgress({ challengerVotes, opponentVotes, total }: { challengerVotes: number; opponentVotes: number; total: number }) {
  const pct = getCompetitionPercent(challengerVotes, opponentVotes);
  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-[10px] font-bold text-foreground-400 mb-1">
        <span>{formatCompact(challengerVotes)} votos</span>
        <span>{formatCompact(opponentVotes)} votos</span>
      </div>
      <div className="w-full h-3 bg-background-100 rounded-full overflow-hidden flex">
        <div
          className="h-full bg-primary-500 rounded-l-full transition-all duration-700"
          style={{ width: `${pct.challenger}%` }}
        ></div>
        <div className="w-[2px] h-full bg-background-50"></div>
        <div
          className="h-full bg-accent-500 rounded-r-full transition-all duration-700"
          style={{ width: `${pct.opponent}%` }}
        ></div>
      </div>
      <div className="flex items-center justify-between text-[10px] font-semibold mt-1">
        <span className="text-primary-600">{pct.challenger}%</span>
        <span className="text-foreground-300">Total: {formatCompact(total)}</span>
        <span className="text-accent-600">{pct.opponent}%</span>
      </div>
    </div>
  );
}

/* ─── Participant Card ─── */
function ParticipantBlock({ p, isWinner, isLeading }: { p: RfwCompetition['challenger']; isWinner?: boolean; isLeading?: boolean }) {
  return (
    <div className={`flex flex-col items-center text-center p-4 ${isWinner ? 'relative' : ''}`}>
      {isWinner && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
          <span className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
            <span className="text-base">👑</span>
          </span>
        </div>
      )}
      <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl overflow-hidden bg-background-100 mb-3 border-2 ${isLeading ? 'border-primary-400 shadow-lg shadow-primary-400/20' : isWinner ? 'border-amber-300 shadow-lg shadow-amber-300/20' : 'border-background-200'}`}>
        {p.logoUrl ? (
          <img src={p.logoUrl} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-background-200">
            <i className="ri-question-line text-2xl text-foreground-300"></i>
          </div>
        )}
      </div>
      <p className="text-sm font-extrabold text-foreground-950 leading-tight">{p.name}</p>
      <p className="text-[11px] text-foreground-400 mt-0.5">{p.subtitle}</p>
      {p.score > 0 && (
        <div className="mt-2">
          <span className="text-sm font-extrabold text-accent-600">{formatCompact(p.score)} pts</span>
        </div>
      )}
    </div>
  );
}

/* ─── Competition Card ─── */
function CompetitionCard({ comp, showPrize }: { comp: RfwCompetition; showPrize?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const isActive = comp.status === 'active';
  const isEnded = comp.status === 'ended';
  const hasWinner = !!comp.winner;
  const leadingSide = comp.challenger.votes >= comp.opponent.votes ? 'challenger' : 'opponent';

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-background-300 transition-all">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-background-100">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
            isActive ? 'bg-emerald-100 text-emerald-700' : isEnded ? 'bg-foreground-100 text-foreground-500' : 'bg-amber-100 text-amber-700'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : isEnded ? 'bg-foreground-400' : 'bg-amber-500'}`}></span>
            {isActive ? 'EN VIVO' : isEnded ? 'FINALIZADA' : 'PRÓXIMAMENTE'}
          </span>
          <span className="text-[10px] text-foreground-400 font-semibold">{tournamentLabels[comp.tournament]}</span>
          <span className="text-[10px] text-foreground-300">·</span>
          <span className="inline-flex items-center gap-1 text-[10px] text-foreground-400">
            <i className={`${votingIcons[comp.votingType]} text-[10px]`}></i>
            {votingLabels[comp.votingType]}
          </span>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[10px] text-primary-600 hover:text-primary-700 font-semibold cursor-pointer whitespace-nowrap flex items-center gap-0.5"
        >
          {expanded ? 'Menos info' : 'Detalles'}
          <i className={expanded ? 'ri-arrow-up-s-line text-[10px]' : 'ri-arrow-down-s-line text-[10px]'}></i>
        </button>
      </div>

      {/* Competition name */}
      <div className="px-4 pt-3">
        <h3 className="text-sm font-extrabold text-foreground-950">{comp.name}</h3>
        <p className="text-[11px] text-foreground-400 mt-0.5 line-clamp-1">{comp.description}</p>
      </div>

      {/* VS Area */}
      <div className="relative px-4 py-4">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <ParticipantBlock p={comp.challenger} isLeading={isActive && leadingSide === 'challenger'} isWinner={isEnded && comp.winner?.id === comp.challenger.id} />
          <div className="relative z-10 flex flex-col items-center gap-1">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent-500 flex items-center justify-center shadow-lg border-2 border-background-50">
              <span className="text-background-50 dark:text-foreground-950 font-black text-xs md:text-sm">VS</span>
            </div>
            {isActive && (
              <span className="text-[9px] font-bold text-foreground-400">{getTimeRemaining(comp.endDate)}</span>
            )}
          </div>
          <ParticipantBlock p={comp.opponent} isLeading={isActive && leadingSide === 'opponent'} isWinner={isEnded && comp.winner?.id === comp.opponent.id} />
        </div>
      </div>

      {/* Progress */}
      {isActive && comp.totalVotes > 0 && (
        <div className="px-4 pb-3">
          <CompetitionProgress challengerVotes={comp.challenger.votes} opponentVotes={comp.opponent.votes} total={comp.totalVotes} />
        </div>
      )}

      {/* Prize */}
      {showPrize && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            {comp.prize.badges.map((b) => (
              <span key={b} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md bg-amber-50 text-[9px] font-bold text-amber-700">
                <i className="ri-award-line text-[10px]"></i>
                {b}
              </span>
            ))}
            <span className="text-[10px] text-emerald-600 font-semibold">+{comp.prize.extraPoints} pts</span>
            <span className="text-base">{comp.prize.trophy === 'gold' ? '🥇' : comp.prize.trophy === 'silver' ? '🥈' : '🥉'}</span>
          </div>
        </div>
      )}

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-background-100 pt-3 space-y-2">
          <p className="text-[11px] text-foreground-500 leading-relaxed">{comp.description}</p>
          <div className="flex items-center gap-2 text-[10px] text-foreground-400">
            <i className="ri-calendar-line"></i>
            {formatCompetitionDates(comp.startDate, comp.endDate)}
          </div>
          {isActive && (
            <div className="flex items-center gap-3 text-[10px]">
              <div className="flex items-center gap-1">
                <span className="w-16 h-1.5 bg-background-200 rounded-full overflow-hidden">
                  <div className="h-full bg-accent-500 rounded-full transition-all" style={{ width: `${comp.progress}%` }}></div>
                </span>
                <span className="font-semibold text-foreground-500">{comp.progress}% completado</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Hall of Fame Card ─── */
function HallOfFameCard({ entry }: { entry: HallOfFameEntry }) {
  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/70 p-4 hover:border-amber-200 transition-all group">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
          <span className="text-base">👑</span>
        </div>
        <div>
          <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">{tournamentLabels[entry.tournament]}</span>
          <span className="text-[10px] text-foreground-400 ml-2">{entry.period}</span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Winner */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 shrink-0 border border-amber-200/50">
              <img src={entry.winnerLogoUrl} alt={entry.winnerName} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-extrabold text-foreground-950 truncate group-hover:text-accent-600 transition-colors">{entry.winnerName}</p>
              <p className="text-[9px] text-foreground-400">{entry.winnerSubtitle}</p>
            </div>
          </div>
        </div>

        {/* VS */}
        <div className="shrink-0 flex flex-col items-center">
          <span className="text-[10px] font-black text-foreground-300">VS</span>
          <span className="text-[9px] font-bold text-accent-600 mt-0.5">{entry.finalScore}</span>
        </div>

        {/* Opponent */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 justify-end text-right">
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground-500 truncate">{entry.opponentName}</p>
            </div>
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 shrink-0 border border-background-200/50 opacity-75">
              <img src={entry.opponentLogoUrl} alt={entry.opponentName} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function CompetenciasPage() {
  const [activeTab, setActiveTab] = useState<MainTab>('activas');
  const [typeFilter, setTypeFilter] = useState<CompetitionType | 'todos'>('todos');

  const filteredActive = useMemo(() => {
    let comps = getActiveCompetitions();
    if (typeFilter !== 'todos') comps = comps.filter(c => c.type === typeFilter);
    return comps;
  }, [typeFilter]);

  const filteredUpcoming = useMemo(() => {
    let comps = getUpcomingCompetitions();
    if (typeFilter !== 'todos') comps = comps.filter(c => c.type === typeFilter);
    return comps;
  }, [typeFilter]);

  const filteredWinners = useMemo(() => {
    let comps = getRecentWinners();
    if (typeFilter !== 'todos') comps = comps.filter(c => c.type === typeFilter);
    return comps;
  }, [typeFilter]);

  const filteredHallOfFame = useMemo(() => {
    let entries = getHallOfFame();
    if (typeFilter !== 'todos') entries = entries.filter(e => e.type === typeFilter);
    return entries;
  }, [typeFilter]);

  const typeFilters: { key: CompetitionType | 'todos'; label: string; icon: string }[] = [
    { key: 'todos', label: 'Todos', icon: 'ri-flashlight-line' },
    { key: 'radio', label: 'Radios', icon: competitionTypeIcons.radio },
    { key: 'programa', label: 'Programas', icon: competitionTypeIcons.programa },
    { key: 'locutor', label: 'Locutores', icon: competitionTypeIcons.locutor },
    { key: 'club', label: 'Clubes', icon: competitionTypeIcons.club },
  ];

  const tabs: { key: MainTab; label: string; icon: string; count: number; color: string }[] = [
    { key: 'activas', label: 'Activas', icon: 'ri-fire-line', count: getActiveCompetitions().length, color: 'text-emerald-600' },
    { key: 'proximas', label: 'Próximas', icon: 'ri-calendar-line', count: getUpcomingCompetitions().length, color: 'text-amber-600' },
    { key: 'ganadores', label: 'Ganadores', icon: 'ri-trophy-line', count: getRecentWinners().length, color: 'text-yellow-600' },
    { key: 'hall-of-fame', label: 'Hall of Fame', icon: 'ri-vip-diamond-line', count: getHallOfFame().length, color: 'text-purple-600' },
  ];

  const records = getRecords();

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">

          {/* HERO */}
          <div className="relative h-56 md:h-72 rounded-2xl overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Epic%20competition%20arena%20with%20dramatic%20lighting%2C%20two%20spotlights%20converging%20on%20center%20stage%2C%20glowing%20trophy%20pedestal%20in%20middle%2C%20warm%20amber%20and%20gold%20spotlights%20against%20dark%20atmospheric%20background%2C%20championship%20battle%20atmosphere%2C%20cinematic%20composition%20with%20dramatic%20shadows%20and%20light%20beams%2C%20prestigious%20competition%20venue%20photography&width=1600&height=900&seq=competencias-hero&orientation=landscape"
              alt="Competencias RFW"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
                <i className="ri-sword-line text-3xl text-amber-400"></i>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
                Competencias RFW
              </h1>
              <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
                Radio vs Radio, Programa vs Programa, Locutor vs Locutor, Club vs Club. La rivalidad que hace crecer a las comunidades. Votá, competí, ganá.
              </p>
              <div className="flex items-center gap-3 mt-5 flex-wrap justify-center">
                <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                  {getActiveCompetitions().length} activas ahora
                </span>
                <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                  {getHallOfFame().length} campeones históricos
                </span>
                <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                  Actualizado en vivo
                </span>
              </div>
            </div>
          </div>

          {/* MAIN TABS */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1 flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-accent-500 text-background-50 shadow-lg'
                    : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.key ? 'bg-background-50/20' : 'bg-background-200'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* TYPE FILTER */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1 flex-wrap">
            <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Filtrar:</span>
            {typeFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => setTypeFilter(f.key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  typeFilter === f.key
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                <i className={`${f.icon} text-[11px]`}></i>
                {f.label}
              </button>
            ))}
          </div>

          {/* CONTENT BY TAB */}
          {/* ─── ACTIVAS ─── */}
          {activeTab === 'activas' && (
            <div className="space-y-6">
              {/* Stats bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Batallas Activas', value: filteredActive.length.toString(), icon: 'ri-fire-line', color: 'text-emerald-600 bg-emerald-100' },
                  { label: 'Votos Totales', value: formatCompact(filteredActive.reduce((s, c) => s + c.totalVotes, 0)), icon: 'ri-heart-line', color: 'text-rose-600 bg-rose-100' },
                  { label: 'Finaliza Próxima', value: filteredActive.length > 0 ? getTimeRemaining(filteredActive[0].endDate) : '—', icon: 'ri-timer-line', color: 'text-amber-600 bg-amber-100' },
                  { label: 'Premio Mayor', value: `+${Math.max(...filteredActive.map(c => c.prize.extraPoints), 0)} pts`, icon: 'ri-flashlight-fill', color: 'text-accent-600 bg-accent-100' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-2xl p-4">
                    <div className={`w-8 h-8 rounded-lg ${stat.color} flex items-center justify-center mb-2`}>
                      <i className={`${stat.icon} text-sm`}></i>
                    </div>
                    <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredActive.map((comp) => (
                  <CompetitionCard key={comp.id} comp={comp} showPrize />
                ))}
              </div>
              {filteredActive.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-fire-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-400">No hay batallas activas en esta categoría</p>
                  <p className="text-[12px] text-foreground-300 mt-1">Revisá las próximas competencias</p>
                </div>
              )}
            </div>
          )}

          {/* ─── PRÓXIMAS ─── */}
          {activeTab === 'proximas' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Próximas Batallas', value: filteredUpcoming.length.toString(), icon: 'ri-calendar-line', color: 'text-amber-600 bg-amber-100' },
                  { label: 'Próximo Torneo', value: filteredUpcoming.length > 0 ? formatCompetitionDates(filteredUpcoming[0].startDate, filteredUpcoming[0].endDate) : '—', icon: 'ri-calendar-check-line', color: 'text-accent-600 bg-accent-100' },
                  { label: 'Premio Mayor', value: `+${Math.max(...filteredUpcoming.map(c => c.prize.extraPoints), 0)} pts`, icon: 'ri-flashlight-fill', color: 'text-purple-600 bg-purple-100' },
                  { label: 'Tipos de Votación', value: `${[...new Set(filteredUpcoming.map(c => votingLabels[c.votingType]))].length}`, icon: 'ri-bar-chart-line', color: 'text-rose-600 bg-rose-100' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-2xl p-4">
                    <div className={`w-8 h-8 rounded-lg ${stat.color} flex items-center justify-center mb-2`}>
                      <i className={`${stat.icon} text-sm`}></i>
                    </div>
                    <p className={`${stat.label === 'Próximo Torneo' ? 'text-xs' : 'text-lg'} font-extrabold text-foreground-950 leading-tight`}>{stat.value}</p>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredUpcoming.map((comp) => (
                  <CompetitionCard key={comp.id} comp={comp} showPrize />
                ))}
              </div>
              {filteredUpcoming.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calendar-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-400">No hay batallas próximas en esta categoría</p>
                </div>
              )}
            </div>
          )}

          {/* ─── GANADORES ─── */}
          {activeTab === 'ganadores' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredWinners.map((comp) => (
                  <CompetitionCard key={comp.id} comp={comp} showPrize />
                ))}
              </div>
              {filteredWinners.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-trophy-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-400">No hay ganadores en esta categoría todavía</p>
                </div>
              )}
            </div>
          )}

          {/* ─── HALL OF FAME ─── */}
          {activeTab === 'hall-of-fame' && (
            <div className="space-y-6">
              {/* Records */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                {records.map((rec) => (
                  <div key={rec.label} className="bg-background-100 rounded-2xl p-4 text-center">
                    <div className={`w-10 h-10 rounded-xl ${rec.color} flex items-center justify-center mx-auto mb-2`}>
                      <i className={`${rec.icon} text-sm`}></i>
                    </div>
                    <p className="text-sm font-extrabold text-foreground-950">{rec.value}</p>
                    <p className="text-[10px] text-foreground-400 mt-0.5">{rec.label}</p>
                    <div className="flex items-center justify-center gap-1 mt-1.5">
                      <div className="w-5 h-5 rounded-full overflow-hidden bg-background-200">
                        <img src={rec.holderLogoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <span className="text-[10px] font-semibold text-foreground-600 truncate">{rec.holder}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                {filteredHallOfFame.map((entry) => (
                  <HallOfFameCard key={entry.id} entry={entry} />
                ))}
              </div>
              {filteredHallOfFame.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-vip-diamond-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-400">No hay campeones en esta categoría</p>
                </div>
              )}
            </div>
          )}

          {/* PRIZES EXPLANATION */}
          <div className="mt-10 bg-background-100 rounded-3xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center">
                <i className="ri-gift-line text-base text-secondary-600"></i>
              </div>
              <h2 className="text-xl font-extrabold text-foreground-950">Sistema de Premios</h2>
            </div>

            <p className="text-sm text-foreground-500 leading-relaxed mb-6">
              Cada competencia otorga premios automáticos según el resultado. Los ganadores reciben insignias, títulos, trofeos y puntos extra de RFW Score. No se pueden comprar ni modificar manualmente.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: 'ri-award-line', title: 'Insignias', desc: 'Cada victoria desbloquea insignias únicas. Coleccionalas todas para tu perfil.', items: ['Campeón Semanal', 'Radio del Mes', 'Club Supremo', 'Voz de la Semana'], color: 'bg-violet-100 text-violet-700' },
                { icon: 'ri-medal-line', title: 'Títulos', desc: 'Títulos honoríficos que aparecen en tu perfil y en los rankings.', items: ['Rey de California', 'Gigante Latino', 'Micrófono de Oro', 'Ejército Supremo'], color: 'bg-amber-100 text-amber-700' },
                { icon: 'ri-trophy-line', title: 'Trofeos', desc: 'Oro, Plata o Bronce según la posición final en cada batalla.', items: ['🥇 Oro — 1er lugar', '🥈 Plata — 2do lugar', '🥉 Bronce — 3er lugar (torneos)'], color: 'bg-yellow-100 text-yellow-700' },
                { icon: 'ri-flashlight-line', title: 'Puntos Extra', desc: 'Bonos directos al RFW Score. Acumulables y permanentes.', items: ['+250 Semanal', '+500 Mensual', '+2000 Anual', '+150 Clubes'], color: 'bg-emerald-100 text-emerald-700' },
              ].map((section) => (
                <div key={section.title} className="bg-background-50 rounded-2xl p-5">
                  <div className={`w-9 h-9 rounded-xl ${section.color} flex items-center justify-center mb-3`}>
                    <i className={`${section.icon} text-sm`}></i>
                  </div>
                  <h4 className="text-sm font-extrabold text-foreground-950 mb-1.5">{section.title}</h4>
                  <p className="text-[11px] text-foreground-500 leading-relaxed mb-3">{section.desc}</p>
                  <div className="space-y-1">
                    {section.items.map((item) => (
                      <span key={item} className="block text-[10px] font-semibold text-foreground-600 pl-2 border-l-2 border-background-200">{item}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CROSS LINKS */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Link to="/rankings" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-bar-chart-2-line text-primary-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Rankings RFW</h4>
                  <p className="text-xs text-foreground-500">Ranking por Score</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/clubes" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-group-line text-accent-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Clubes</h4>
                  <p className="text-xs text-foreground-500">Batallas de clubes</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/gamificacion" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-emerald-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Gamificación</h4>
                  <p className="text-xs text-foreground-500">Sistema de logros</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/insignias" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <i className="ri-award-line text-violet-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Insignias</h4>
                  <p className="text-xs text-foreground-500">Colección de logros</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

        </div>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>
    </div>
  );
}